import { useState, useEffect, useCallback } from 'react';

/**
 * Keyboard scope for the finance module. Only the triage modal binds keys now:
 * D/E/X pick per-kind picker tiles, Enter confirms, Esc closes, S skips, R
 * requests donor info, P picks a parent. The former `list` scope (J/K nav,
 * C/L/E/D classify, X toggle-check, ⌘/Ctrl+Enter save) was deleted once its
 * sole consumer migrated to the triage modal, leaving that keymap unreachable.
 */
export type KeyboardShortcutScope = 'triage';

/**
 * USI-007 / ADR-014: triage-tile discriminator for the per-kind picker keymap.
 * - `'contribution'` (D) — credit bank-line → contribution kind
 * - `'expense'` (E) — debit bank-line → expense kind
 * - `'exclude'` (X) — bank-line → Exclude from report (ADR-014: collapses the
 *   retired `'transfer'` / `'not-campaign'` tiles into one off-ramp; the retired
 *   `'check-deposit'` tile, 2026-07-14, also resolves here now)
 */
export type TriagePickerTile =
  | 'contribution'
  | 'expense'
  | 'exclude';

interface UseFinanceKeyboardShortcutsOptions {
  /** Esc handler. */
  onClose: () => void;

  // ----- Triage-scope props -----
  /** Triage: D/E/X tile picker. */
  onTriagePick?: (tile: TriagePickerTile) => void;
  /** Triage: Enter confirms the current row's body. */
  onTriageConfirm?: () => void;
  /** Triage: S advances to the next item (or closes at end). */
  onTriageSkip?: () => void;
  /**
   * Triage: R fires the "Mark as information requested" attestation on the
   * missing-info-contribution body (USI-010). Per CONVENTIONS.md § Keyboard
   * shortcuts. Other kinds ignore R via the modal's per-kind dispatch.
   */
  onTriageRequestDonorInfo?: () => void;
  /**
   * Triage: P fires "Pick parent" on the orphan-refund body (USI-011). When a
   * suggestion is highlighted, P confirms it; otherwise P opens the search-all
   * panel. Other kinds ignore P via the modal's per-kind dispatch.
   */
  onTriagePickParent?: () => void;

  /** Scope discriminator; only `'triage'` remains. Defaults to `'triage'`. */
  scope?: KeyboardShortcutScope;
}

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * USI-007: shortcut hook for the triage modal. Decoupled from any global store
 * — the modal passes its own callbacks so it can manage its session queue
 * locally.
 *
 * Keymap (`scope: 'triage'`): D/E/X pick tile, Enter confirm, S skip, R request
 * donor info, P pick parent, Esc close, ? toggle hint.
 */
export function useFinanceKeyboardShortcuts({
  onClose,
  onTriagePick,
  onTriageConfirm,
  onTriageSkip,
  onTriageRequestDonorInfo,
  onTriagePickParent,
  scope = 'triage',
}: UseFinanceKeyboardShortcutsOptions) {
  const [showShortcutReference, setShowShortcutReference] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't fire when typing in form fields
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag && INPUT_TAGS.has(tag)) return;

      // ? toggles the help overlay.
      if (e.key === '?') {
        setShowShortcutReference((prev) => !prev);
        return;
      }
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (scope === 'triage') {
        // Triage modal keymap — single-key picker tiles + Enter/Skip.
        switch (e.key) {
          case 'd':
          case 'D':
            onTriagePick?.('contribution');
            return;
          case 'e':
          case 'E':
            onTriagePick?.('expense');
            return;
          case 'x':
          case 'X':
            // ADR-014: X = Exclude from report (collapses the retired T/N tiles).
            onTriagePick?.('exclude');
            return;
          case 's':
          case 'S':
            onTriageSkip?.();
            return;
          case 'r':
          case 'R':
            // USI-010: R fires "Mark as information requested" on the
            // missing-info-contribution body. The modal's per-kind dispatcher
            // decides whether to route the key into the active body — other
            // kinds ignore it.
            onTriageRequestDonorInfo?.();
            return;
          case 'p':
          case 'P':
            // USI-011: P fires "Pick parent" on the orphan-refund body
            // (confirms highlighted suggestion / opens search panel). Other
            // kinds ignore P via the modal's per-kind dispatch.
            onTriagePickParent?.();
            return;
          case 'Enter':
            onTriageConfirm?.();
            return;
          default:
            return;
        }
      }
    },
    [
      onClose, onTriagePick, onTriageConfirm, onTriageSkip,
      onTriageRequestDonorInfo, onTriagePickParent, scope,
    ],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { showShortcutReference, setShowShortcutReference };
}
